import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const date = z.string().date()
const timestamp = z.string().datetime({ offset: true })
const boundedText = (maximum: number) => z.string().trim().min(1).max(maximum)

export const serviceAgreementStateV1Schema = z.enum([
  "draft",
  "pending_approval",
  "active",
  "paused",
  "pending_cancellation",
  "canceled",
  "completed",
])

export const serviceAgreementRecurrenceV1Schema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("weekly"),
    intervalWeeks: z.number().int().min(1).max(52),
    weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    localTime: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    timezone: boundedText(100),
  }).strict().superRefine((value, context) => {
    if (new Set(value.weekdays).size !== value.weekdays.length) {
      context.addIssue({
        code: "custom",
        path: ["weekdays"],
        message: "Weekdays must be unique.",
      })
    }
  }),
  z.object({
    kind: z.literal("monthly"),
    intervalMonths: z.number().int().min(1).max(24),
    dayOfMonth: z.number().int().min(1).max(28),
    localTime: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    timezone: boundedText(100),
  }).strict(),
])

const commercialRevision = z.object({
  revisionId: entityIdSchema,
  effectiveFrom: date,
  scope: boundedText(10_000),
  lineItems: z.array(z.object({
    serviceId: entityIdSchema,
    pricingSnapshotId: entityIdSchema,
  }).strict()).min(1).max(100),
  billingMode: boundedText(100),
  invoiceable: z.boolean(),
  recurrence: serviceAgreementRecurrenceV1Schema,
  escalation: z.object({
    kind: z.enum(["none", "percentage", "fixed"]),
    value: z.number().int().min(0).max(9_007_199_254_740_991),
    noticeDays: z.number().int().min(0).max(365),
    customerAccepted: z.boolean(),
  }).strict(),
  renewal: z.object({
    kind: z.enum(["none", "manual", "automatic"]),
    termEnd: date.nullable(),
    noticeDays: z.number().int().min(0).max(365),
  }).strict(),
  cancellation: z.object({
    noticeDays: z.number().int().min(0).max(365),
    earlyTerminationFeeMinor: z.number().int().min(0).max(9_007_199_254_740_991),
  }).strict(),
}).strict()

export const serviceAgreementCreateV1Schema = z.object({
  serviceAgreementId: entityIdSchema,
  customerId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  billingProfileId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  ...commercialRevision.shape,
}).strict()

export const serviceAgreementActivateV1Schema = z.object({
  serviceAgreementId: entityIdSchema,
  revisionId: entityIdSchema,
  approvedAt: timestamp,
}).strict()

export const serviceAgreementReviseV1Schema = z.object({
  serviceAgreementId: entityIdSchema,
  previousRevisionId: entityIdSchema,
  changeBasis: z.enum(["customer_approved", "accepted_escalator"]),
  approvedAt: timestamp,
  ...commercialRevision.shape,
}).strict()

export const serviceAgreementGenerateV1Schema = z.object({
  serviceAgreementId: entityIdSchema,
  throughDate: date,
  generatedAt: timestamp,
}).strict()

export const serviceAgreementCancelV1Schema = z.object({
  serviceAgreementId: entityIdSchema,
  effectiveCancellationDate: date,
  terminationFeeMinor: z.number().int().min(0).max(9_007_199_254_740_991),
  reason: boundedText(2_000),
  occurredAt: timestamp,
}).strict()

export type ServiceAgreementCreateV1 = z.infer<
  typeof serviceAgreementCreateV1Schema
>
export type ServiceAgreementReviseV1 = z.infer<
  typeof serviceAgreementReviseV1Schema
>
